import { Ref, useImperativeHandle, useMemo } from 'react';
import { useFieldArray, UseFieldArrayMethods, useWatch } from 'react-hook-form';
import { useUpdateEffect } from 'react-use';

import { useFormController } from '@dreryk/react-common';

import { RecipientCategory, UnsentPrescription } from '~libs.shared.prescription';

import { PatientId } from '../../PrescriptionIssuingPanel.types';

import {
   DraftPrescriptionField,
   DraftPrescriptionsFieldArray,
   DraftPrescriptionsFieldArrayMethods,
   DraftPrescriptionsFormController,
} from './DraftPrescriptionsForm.types';
import { mapPrescriptionPackagesToDraftPrescriptionFields } from './DraftPrescriptionsForm.utils';

export const useDraftPrescriptionsFormController = (
   unsentPrescriptions: UnsentPrescription[],
   initialRecipientCategory: RecipientCategory,
   patientId: PatientId
): DraftPrescriptionsFormController => {
   const initialValues = useMemo(
      () => ({
         prescriptionCategory: initialRecipientCategory,
         draftPrescriptions: mapPrescriptionPackagesToDraftPrescriptionFields(unsentPrescriptions, patientId),
      }),
      [unsentPrescriptions, patientId, initialRecipientCategory]
   );

   const formController: DraftPrescriptionsFormController = useFormController(initialValues, {
      shouldFocusError: true,
      shouldUnregister: false,
   });
   const { reset } = formController;

   useUpdateEffect(() => {
      reset(initialValues);
   }, [initialValues]);

   return formController;
};

export const useDraftPrescriptionsFieldArray = (
   control: DraftPrescriptionsFormController['control'],
   draftPrescriptionsFieldArrayMethodsRef: Ref<DraftPrescriptionsFieldArrayMethods>
): UseFieldArrayMethods<DraftPrescriptionField> => {
   const fieldArray = useFieldArray<DraftPrescriptionField>({
      name: 'draftPrescriptions',
      control: control,
   });
   useImperativeHandle(draftPrescriptionsFieldArrayMethodsRef, () => fieldArray, [fieldArray]);
   return fieldArray;
};

export const useDraftPrescriptionsWatch = (
   control: DraftPrescriptionsFormController['control']
): DraftPrescriptionsFieldArray => {
   return useWatch<DraftPrescriptionsFieldArray>({
      control: control,
      name: 'draftPrescriptions',
      defaultValue: [],
   });
};
